import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Printer, Loader2, ChefHat } from "lucide-react";

function MenuPrintBar() {
  const [busy, setBusy] = useState(false);
  const call = async (layout, printOffice) => {
    setBusy(true);
    try {
      const body = { layout, title: "SNACKBAR", subtitle: "Alle Preise inkl. MwSt.", include_all_products: true };
      if (printOffice) {
        await api.post("/admin/signage/price-signs?print_office=true", body);
        toast.success("An Bürodrucker gesendet");
      } else {
        const { data } = await api.post("/admin/signage/price-signs", body, { responseType: "blob" });
        const url = URL.createObjectURL(data);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  const Btn = ({ label, layout, printOffice, tone, testid, Icon }) => (
    <button onClick={() => call(layout, printOffice)} disabled={busy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded border transitions-safe disabled:opacity-40"
      style={{ borderColor: `${tone}66`, color: tone }}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Btn label="Speisekarte · Vorschau" layout="menu" printOffice={false} tone="#EEDC82" testid="snackbar-menu-preview" Icon={FileText} />
      <Btn label="Speisekarte · Drucken" layout="menu" printOffice={true} tone="#D4AF37" testid="snackbar-menu-print" Icon={Printer} />
      <Btn label="Preisschilder · Vorschau" layout="individual" printOffice={false} tone="#EEDC82" testid="snackbar-cards-preview" Icon={FileText} />
      <Btn label="Preisschilder · Drucken" layout="individual" printOffice={true} tone="#D4AF37" testid="snackbar-cards-print" Icon={Printer} />
    </div>
  );
}

const COLS = [
  { s: "neu", l: "Neu", color: "border-[#5B1319]" },
  { s: "angenommen", l: "Angenommen", color: "border-[#EEDC82]/50" },
  { s: "vorbereitung", l: "In Vorbereitung", color: "border-[#D4AF37]" },
  { s: "abholbereit", l: "Abholbereit", color: "border-[#2E7D32]" },
  { s: "ausgegeben", l: "Ausgegeben", color: "border-[#2A2A2A]" },
];

const NEXT = { neu: "angenommen", angenommen: "vorbereitung", vorbereitung: "abholbereit", abholbereit: "ausgegeben" };

export default function Snackbar() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/snack-orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const advance = async (o) => {
    const next = NEXT[o.status];
    if (!next) return;
    await api.patch(`/snack-orders/${o.id}/status`, { status: next });
    toast.success(`Bestellung ${o.number}: ${next}`);
    load();
  };
  const cancel = async (o) => {
    await api.patch(`/snack-orders/${o.id}/status`, { status: "storniert" });
    load();
  };

  return (
    <div className="space-y-6" data-testid="snackbar-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Küche</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3"><ChefHat size={30} className="text-[#D4AF37]" /> Snackbar</h1>
        </div>
        <MenuPrintBar />
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
        {COLS.map((c) => (
          <div key={c.s} className={`card-lux border-l-4 ${c.color}`}>
            <div className="px-4 py-3 border-b border-[#1A1A1A] flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-[#A8A398]">{c.l}</div>
              <div className="text-[#D4AF37] font-mono text-sm">{orders.filter((o) => o.status === c.s).length}</div>
            </div>
            <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
              {orders.filter((o) => o.status === c.s).map((o) => (
                <div key={o.id} className="bg-[#050505] border border-[#2A2A2A] rounded p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-mono text-lg text-[#D4AF37]">#{o.number}</div>
                    <div className="text-xs text-[#A8A398]">{o.start_time || ""}</div>
                  </div>
                  <div className="text-xs text-[#A8A398] truncate">{o.guest_name}</div>
                  <div className="mt-2 space-y-1 text-sm">
                    {o.items.map((i, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{i.quantity}× {i.name}</span>
                        <span className="font-mono text-[#A8A398]">{i.line_total.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                  {o.status !== "ausgegeben" && (
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => advance(o)} className="flex-1 text-xs uppercase tracking-widest bg-[#D4AF37] text-black py-1.5 rounded">
                        Weiter →
                      </button>
                      <button onClick={() => cancel(o)} className="text-xs px-3 py-1.5 border border-[#2A2A2A] text-[#A8A398] rounded">✕</button>
                    </div>
                  )}
                </div>
              ))}
              {!orders.filter((o) => o.status === c.s).length && (
                <div className="text-center text-xs text-[#A8A398] py-6">—</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
